import CrownIcon from 'assets/Icons/CrownIcon';
import React, { FC, useState } from 'react';
import { useRecoilValue } from 'recoil';

import {
  getStructuredPlanData,
  SubscriptionButtonTitle,
} from '@/components/Subscription/PlanTable/Columns';
import CheckPoints from '@/components/Subscription/PlanTable/headerColumn/checkpoints';
import Item from '@/components/Subscription/PlanTable/ResponsiveTable/Item';

import { user } from '@/atoms/user';
import { IPlan, PlanNameEnum } from '@/utils/interfaces/Subscription';

export interface plansInterface {
  id: number;
  plan: string;
  bestValue: boolean;
  price: string;
  priceCut: string;
  buttonText: string;
  buttonTooltip: string;
  whatInside: string;
  digital: string;
  hard: string;
  duration: string;
  validity: string;
  freeShipping: boolean;
  appAccess: boolean;
  title: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  disabled: boolean;
}

interface Props {
  plans?: IPlan[];
  setSelectedPlan: React.Dispatch<React.SetStateAction<IPlan | undefined>>;
  subscriptionHandle: (buttonTitle: SubscriptionButtonTitle) => Promise<void>;
}

const ResponsiveTable: FC<Props> = ({
  plans,
  setSelectedPlan,
  subscriptionHandle,
}) => {
  const plPlan = plans?.find((i) => i.name === PlanNameEnum.Plus);
  const pmPlan = plans?.find((i) => i.name === PlanNameEnum.Premium);
  const stPlan = plans?.find((i) => i.name === PlanNameEnum.Standard);
  const userState = useRecoilValue(user);

  const plansSt: plansInterface[] = [
    {
      id: 0,
      plan: stPlan ? getStructuredPlanData(stPlan, userState).whatInside : '',
      title: stPlan ? getStructuredPlanData(stPlan, userState).title : '',
      bestValue: stPlan
        ? getStructuredPlanData(stPlan, userState).bestValue
        : false,
      price: stPlan ? getStructuredPlanData(stPlan, userState).price : '',
      priceCut: '5070',
      buttonText: stPlan
        ? getStructuredPlanData(stPlan, userState).buttonTitle
        : '',
      buttonTooltip: stPlan
        ? getStructuredPlanData(stPlan, userState).buttonTooltip
        : '',
      appAccess: stPlan
        ? getStructuredPlanData(stPlan, userState).appAccess
        : false,
      digital: stPlan ? getStructuredPlanData(stPlan, userState).digital : '',
      hard: stPlan ? getStructuredPlanData(stPlan, userState).hard : '',
      duration: stPlan ? getStructuredPlanData(stPlan, userState).duration : '',
      freeShipping: stPlan
        ? getStructuredPlanData(stPlan, userState).freeShipping
        : false,
      validity: stPlan ? getStructuredPlanData(stPlan, userState).validity : '',
      whatInside: stPlan
        ? getStructuredPlanData(stPlan, userState).whatInside
        : '',
      onClick: () => {
        setSelectedPlan(stPlan);
        stPlan &&
          subscriptionHandle(
            getStructuredPlanData(stPlan, userState).buttonTitle,
          );
      },
      disabled: stPlan
        ? getStructuredPlanData(stPlan, userState).disabled
        : false,
    },
    {
      id: 1,
      plan: pmPlan ? getStructuredPlanData(pmPlan, userState).whatInside : '',
      title: pmPlan ? getStructuredPlanData(pmPlan, userState).title : '',
      bestValue: pmPlan
        ? getStructuredPlanData(pmPlan, userState).bestValue
        : false,
      price: pmPlan ? getStructuredPlanData(pmPlan, userState).price : '',
      priceCut: '20,280',
      buttonText: pmPlan
        ? getStructuredPlanData(pmPlan, userState).buttonTitle
        : '',
      buttonTooltip: pmPlan
        ? getStructuredPlanData(pmPlan, userState).buttonTooltip
        : '',
      appAccess: pmPlan
        ? getStructuredPlanData(pmPlan, userState).appAccess
        : false,
      digital: pmPlan ? getStructuredPlanData(pmPlan, userState).digital : '',
      hard: pmPlan ? getStructuredPlanData(pmPlan, userState).hard : '',
      duration: pmPlan ? getStructuredPlanData(pmPlan, userState).duration : '',
      freeShipping: pmPlan
        ? getStructuredPlanData(pmPlan, userState).freeShipping
        : false,
      validity: pmPlan ? getStructuredPlanData(pmPlan, userState).validity : '',
      whatInside: pmPlan
        ? getStructuredPlanData(pmPlan, userState).whatInside
        : '',
      onClick: () => {
        setSelectedPlan(pmPlan);
        pmPlan &&
          subscriptionHandle(
            getStructuredPlanData(pmPlan, userState).buttonTitle,
          );
      },
      disabled: pmPlan
        ? getStructuredPlanData(pmPlan, userState).disabled
        : false,
    },
  ];

  const plansSt2: plansInterface[] = [
    {
      id: 0,
      plan: plPlan ? getStructuredPlanData(plPlan, userState).whatInside : '',
      title: plPlan ? getStructuredPlanData(plPlan, userState).title : '',
      bestValue: plPlan
        ? getStructuredPlanData(plPlan, userState).bestValue
        : false,
      price: plPlan ? getStructuredPlanData(plPlan, userState).price : '',
      priceCut: '11,880',
      buttonText: plPlan
        ? getStructuredPlanData(plPlan, userState).buttonTitle
        : '',
      buttonTooltip: plPlan
        ? getStructuredPlanData(plPlan, userState).buttonTooltip
        : '',
      appAccess: plPlan
        ? getStructuredPlanData(plPlan, userState).appAccess
        : false,
      digital: plPlan ? getStructuredPlanData(plPlan, userState).digital : '',
      hard: plPlan ? getStructuredPlanData(plPlan, userState).hard : '',
      duration: plPlan ? getStructuredPlanData(plPlan, userState).duration : '',
      freeShipping: plPlan
        ? getStructuredPlanData(plPlan, userState).freeShipping
        : false,
      validity: plPlan ? getStructuredPlanData(plPlan, userState).validity : '',
      whatInside: plPlan
        ? getStructuredPlanData(plPlan, userState).whatInside
        : '',
      onClick: () => {
        setSelectedPlan(plPlan);
        plPlan &&
          subscriptionHandle(
            getStructuredPlanData(plPlan, userState).buttonTitle,
          );
      },
      disabled: plPlan
        ? getStructuredPlanData(plPlan, userState).disabled
        : false,
    },
  ];

  // ✅ open all by default
  const [data, setData] = useState<number[]>(plansSt.map((p) => p.id));
  const [data2, setData2] = useState<number[]>(plansSt2.map((p) => p.id));

  return (
    <div className='px-4 py-8'>
      <div className='flex flex-col gap-4 mb-6'>
        <div className='flex items-center gap-3 '>
          <CrownIcon width={30} height={30} />
          <p className='text-xl text-white'>Explore our plans</p>
        </div>
        <CheckPoints />
      </div>
      <div className='flex flex-col gap-4'>
        <div className='border border-grayZl-900 rounded-xl overflow-hidden'>
          <div className='flex flex-col gap-1 items-center justify-center px-6 py-3 border-b border-white/60 bg-dropDownMenuBg'>
            <p className='text-lg text-white font-semibold text-center'>
              ZebraLearn Lite
            </p>
            <p className='text-base font-normal'>Digital Only</p>
          </div>
          {plansSt2.map((item, index) => (
            <Item
              key={index}
              index={index}
              item={item}
              setData={setData2}
              data={data2}
              items={plansSt2}
            />
          ))}
        </div>
        <div className='border border-grayZl-900 rounded-xl overflow-hidden'>
          <div className='flex flex-col gap-1 items-center justify-center px-6 py-3 border-b border-white/60 bg-dropDownMenuBg'>
            <p className='text-lg text-white font-semibold text-center'>
              ZebraLearn Prime
            </p>
            <p className='text-base font-normal'>Digital + Hard Cover</p>
          </div>
          {plansSt.map((item, index) => (
            <Item
              key={index}
              index={index}
              item={item}
              setData={setData}
              data={data}
              items={plansSt}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ResponsiveTable;
